/**
 * De ECharts-omhulling waar elke grafiek doorheen gaat.
 *
 * Bewust een eigen wikkel en geen kant-en-klare React-binding: die haalt de
 * hele bibliotheek binnen, terwijl we hier maar zes grafiektypen gebruiken.
 * De import hieronder is boomgeschud — alleen wat we tekenen komt in de bundel.
 *
 * De wikkel doet drie dingen die anders in elke grafiek zouden terugkomen: hij
 * schaalt mee met het paneel, hij geeft een nette lege staat in plaats van een
 * zwart vlak, en hij ruimt de instantie op als het paneel verdwijnt.
 */
import { useEffect, useRef, useState } from 'react'
import type * as EChartsType from 'echarts/core'

/**
 * ECharts wordt LUI geladen.
 *
 * De bibliotheek is ruim een halve megabyte; pagina's zonder grafiek — de
 * chats, de instellingen, een chatvenster — hoeven daar niet op te wachten.
 * Deze belofte wordt één keer gemaakt en daarna gedeeld, dus acht grafieken
 * op één scherm halen hem samen één keer op.
 */
let laden: Promise<typeof EChartsType> | null = null

function laadECharts(): Promise<typeof EChartsType> {
  if (!laden) {
    laden = (async () => {
      const [core, charts, componenten, renderer] = await Promise.all([
        import('echarts/core'),
        import('echarts/charts'),
        import('echarts/components'),
        import('echarts/renderers'),
      ])
      core.use([
        charts.BarChart, charts.LineChart, charts.PieChart, charts.FunnelChart,
        componenten.GridComponent, componenten.TooltipComponent,
        componenten.LegendComponent, componenten.MarkLineComponent,
        renderer.CanvasRenderer,
      ])
      return core
    })()
  }
  return laden
}

export function Chart({
  option,
  height,
  empty,
}: {
  /** De volledige ECharts-optie; stel hem samen met de helpers uit `theme`. */
  option: Record<string, unknown>
  height: number
  /** True als er niets te tonen valt; dan komt de placeholder in beeld. */
  empty?: boolean
}) {
  const vak = useRef<HTMLDivElement | null>(null)
  const grafiek = useRef<EChartsType.ECharts | null>(null)
  const [klaar, setKlaar] = useState(false)

  useEffect(() => {
    if (empty) return
    let weg = false
    let waarnemer: ResizeObserver | null = null
    laadECharts().then((echarts) => {
      if (weg || !vak.current) return
      const inst = echarts.init(vak.current, undefined, { renderer: 'canvas' })
      grafiek.current = inst
      setKlaar(true)
      // Meeschalen met het paneel; een vaste breedte breekt zodra de zijbalk
      // in- of uitklapt of het venster verandert.
      waarnemer = new ResizeObserver(() => inst.resize())
      waarnemer.observe(vak.current)
    })
    return () => {
      weg = true
      waarnemer?.disconnect()
      grafiek.current?.dispose()
      grafiek.current = null
      setKlaar(false)
    }
  }, [empty])

  useEffect(() => {
    if (!grafiek.current || !klaar) return
    // `true` wist de vorige reeksen; zonder dat blijven oude lijnen staan
    // wanneer een filter het aantal reeksen verkleint.
    grafiek.current.setOption(option, true)
  }, [option, klaar])

  if (empty) {
    return (
      <div className="chartempty" style={{ height }}>
        <span className="ico" aria-hidden="true">◠</span>
        No data in this window
      </div>
    )
  }
  // 18-10-2026: onder de dashboardschaal (CSS zoom op <html>, lib/schaal)
  // rekent ECharts de muis verkeerd om: wie op balk C wijst, krijgt de tooltip
  // van B (gemeten in Chromium 131 en Firefox 132). De grafiek krijgt daarom
  // een tegen-zoom, zodat hij zelf op 1 staat, en een hoogte maal --dz, zodat
  // hij even groot blijft. Breedte 100% volgt het paneel al goed.
  return (
    <div ref={vak} style={{
      width: '100%',
      height: `calc(${height}px * var(--dz, 1))`,
      zoom: 'calc(1 / var(--dz, 1))',
    }} />
  )
}
